/**
 * The pivoted statement.
 *
 * Rendered whole -- no virtualisation. Tables here are read by scanning, and
 * a virtualised table breaks ctrl-F, which is how people actually search one
 * (§13).
 *
 * Every cell links to the document and page it came from, which is the thing
 * that makes a cross-period table checkable rather than merely readable.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'wouter'
import type { Pivot, PivotCell } from '@server/sql/facts.ts'
import { Raw, RawNumber } from '~/components/ui/raw.tsx'
import { useT } from '~/i18n/index.tsx'
import { date } from '~/i18n/format.ts'
import { cn } from '~/lib/cn.ts'
import { pickDigits, useDigits } from '~/features/viewer/digits.tsx'

/**
 * Is this container scrolled away from its inline start?
 *
 * A frozen column with nothing marking its edge reads as a rendering
 * artefact -- the numbers simply run underneath the labels. The shadow only
 * belongs there while there is something hidden behind it, so this is a real
 * measurement rather than a permanent decoration.
 *
 * RTL makes `scrollLeft` negative in every browser that follows the spec, so
 * the test is on the magnitude.
 */
function useScrolledFromStart<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [scrolled, setScrolled] = useState(false)

  const check = useCallback(() => {
    const el = ref.current
    if (el) setScrolled(Math.abs(el.scrollLeft) > 1)
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    check()
    el.addEventListener('scroll', check, { passive: true })
    return () => el.removeEventListener('scroll', check)
  }, [check])

  return { ref, scrolled }
}

export function PivotTable({
  pivot, symbol, rtl,
}: { pivot: Pivot; symbol: string; rtl: boolean }) {
  const { locale, v } = useT()
  const { ref, scrolled } = useScrolledFromStart<HTMLDivElement>()

  // The frozen label column. 18rem is right on a desktop and is 74% of a
  // 390px phone, where it leaves nothing to scroll into view -- so the
  // minimum is a breakpoint, not a constant.
  const labelCol = 'sticky start-0 min-w-[10rem] sm:min-w-[18rem]'

  // The direction is known here from the *data*, not from the UI language,
  // so the shadow falls on the side the numbers actually scroll under.
  const frozenEdge = scrolled
    ? { boxShadow: `${rtl ? '-' : ''}8px 0 8px -8px rgb(0 0 0 / 0.22)` }
    : undefined

  return (
    <div
      ref={ref}
      className="overflow-x-auto overscroll-x-contain rounded-[var(--radius-container)] border border-[var(--border)]"
    >
      {/* `w-max` rather than `w-full`: a twelve-period pivot is wider than
          the pane, and letting the table shrink to fit crushes the label
          column to one character per line. It takes its natural width and the
          container scrolls. */}
      <table
        className="w-max min-w-full border-collapse text-[length:var(--text-data)]"
        dir={rtl ? 'rtl' : 'ltr'}
      >
        <thead>
          <tr>
            <th
              className={cn(labelCol, 'z-20 border-b border-[var(--border)] bg-[var(--bg-sunken)] px-2 py-1.5 text-start')}
              style={frozenEdge}
            />
            {pivot.columns.map((c) => (
              <th
                key={c.key}
                className="whitespace-nowrap border-b border-s border-[var(--border)] bg-[var(--bg-sunken)] px-2 py-1.5 text-end align-bottom"
              >
                {/* The filing this column is, in the reader's language... */}
                {c.fiscalYear !== undefined && (
                  <span dir="ltr" className="tnum block whitespace-nowrap font-medium text-[var(--text)]">
                    {c.period ? (v('periodShort', c.period) ?? c.period) : ''} {c.fiscalYear}
                  </span>
                )}
                {/* ...and the label as printed, which is what you match
                    against the page image. */}
                {c.labelRaw && (
                  <Raw
                    lang={rtl ? 'ar' : null}
                    className="mt-0.5 block whitespace-pre-line text-[length:var(--text-micro)] font-normal text-[var(--text-muted)]"
                  >
                    {c.labelRaw}
                  </Raw>
                )}
                {c.periodEnd && (
                  <span dir="ltr" className="mt-0.5 block text-[length:var(--text-micro)] font-normal text-[var(--text-faint)]">
                    {date(c.periodEnd, locale)}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pivot.rows.map((r) => (
            <tr
              key={r.key}
              className={cn(
                'border-b border-[var(--border)] last:border-b-0',
                'hover:bg-[var(--bg-sunken)]/50 active:bg-[var(--bg-sunken)]/50',
                r.isTotal && 'bg-[var(--bg-sunken)]/60',
              )}
            >
              <th
                scope="row"
                className={cn(
                  labelCol,
                  'z-10 max-w-[28rem] bg-[var(--bg-raised)] px-2 py-[5px] text-start align-top font-normal',
                  r.isTotal && 'font-semibold',
                )}
                style={{ ...frozenEdge, paddingInlineStart: 8 + (r.level ?? 0) * 12 }}
              >
                <Raw lang={r.labelLang}>{r.labelRaw}</Raw>
                {r.noteRef && (
                  <sup className="ms-1 text-[length:var(--text-micro)] text-[var(--text-faint)]">
                    <Raw lang={r.labelLang}>{r.noteRef}</Raw>
                  </sup>
                )}
              </th>
              {pivot.columns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'whitespace-nowrap border-s border-[var(--border)] px-2 py-[5px] text-end align-top',
                    r.isTotal && 'font-semibold',
                  )}
                >
                  <Cell cell={r.cells[c.key] ?? null} symbol={symbol} lang={r.labelLang} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Cell({
  cell, symbol, lang,
}: { cell: PivotCell | null; symbol: string; lang: string | null }) {
  const mode = useDigits()
  const { t } = useT()

  // A blank cell is a label this filing did not carry. It is not a zero, and
  // with rows matched on printed label it is often a wording change rather
  // than a missing figure -- so it reads as absent, not as nothing.
  if (!cell) return <span className="text-[var(--text-faint)]">·</span>

  const { text, isFallback } = pickDigits(mode, cell.raw, cell.western)
  if (text === null) return <span className="text-[var(--text-faint)]">·</span>

  const number = <RawNumber value={text} lang={isFallback ? null : lang} />

  if (cell.pageNo === null) return number

  // The link is stretched back over the cell's own padding, so the target is
  // the whole cell rather than the width of the digits in it. A four-digit
  // figure is a 30px-wide tap target otherwise.
  return (
    <Link
      href={`/c/${symbol}/d/${cell.documentId}?p=${cell.pageNo}`}
      title={t('fin.goToPage', { n: cell.pageNo })}
      className={cn(
        '-mx-2 -my-[5px] block px-2 py-[5px] transition-colors duration-75',
        'hover:text-[var(--accent)] hover:underline hover:decoration-dotted hover:underline-offset-2',
        'active:bg-[var(--accent-soft)] active:text-[var(--accent)]',
      )}
    >
      {number}
    </Link>
  )
}
